import { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import agentAdminService from '@wlb/shared/api/agent/agentAdminService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { Icon } from '../components/ui/Icon';
import { IconButton } from '../components/ui/IconButton';
import { StateBlock } from '../components/ui/StateBlock';
import { StatusPill } from '../components/ui/StatusPill';
import { TabNav, type TabNavItem } from '../components/ui/TabNav';
import { useAsyncResource } from '../hooks/useAsyncResource';
import type { AgentDetailContext } from '../features/agents/agentForm';
import { resolvePictureUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

/**
 * Agent detail shell.
 *
 * The record is loaded once here and handed to the sub-modules through the
 * outlet, so moving between profile and edit swaps only the panel below the
 * header: no refetch, no spinner, no scroll jump. Each sub-module is a real
 * route, so it is bookmarkable and the back button walks back through the tabs
 * the operator opened.
 */
export function AgentDetails() {
  const { t } = useTranslation('admin');
  const { agentId = '' } = useParams<{ agentId: string }>();
  const location = useLocation();
  const [statusPending, setStatusPending] = useState(false);

  const numericAgentId = Number(agentId);
  const isValidId = Number.isSafeInteger(numericAgentId) && numericAgentId > 0;

  const load = useCallback(
    () => agentAdminService.getAgent(numericAgentId),
    [numericAgentId],
  );
  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    String(agentId),
    t('common.errorTitle'),
    isValidId,
    'agent',
  );

  /**
   * The list hands over the filters it was showing, so "back to agents" returns
   * to the exact page and search the operator left instead of a reset table.
   */
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch;
  const backTo = `/agents${listSearch ?? ''}`;

  const toggleActive = async () => {
    if (!data) {
      return;
    }
    setStatusPending(true);
    try {
      await agentAdminService.setAgentActive(data.agentId, !data.isActive);
      ToastService.show({
        type: 'success',
        message: data.isActive
          ? t('agents.toasts.deactivated')
          : t('agents.toasts.activated'),
      });
      reload();
    } catch (cause) {
      ToastService.show({
        type: 'error',
        message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
      });
    } finally {
      setStatusPending(false);
    }
  };

  const tabs = useMemo<readonly TabNavItem[]>(
    () => [
      {
        to: `/agents/${agentId}`,
        label: t('agents.details.tabs.profile'),
        icon: 'users',
        tone: 'blue',
        end: true,
      },
      {
        to: `/agents/${agentId}/edit`,
        label: t('agents.details.tabs.edit'),
        icon: 'edit',
        tone: 'indigo',
      },
    ],
    [t, agentId],
  );

  const displayName = data
    ? data.userName || data.fullName || t('common.none')
    : t('agents.details.title');

  const context: AgentDetailContext | null = data ? { agent: data, reload } : null;

  return (
    <div className="page">
      <header className="page__header page__header--entity">
        {data ? (
          <div className="page__identity">
            <Avatar src={resolvePictureUrl(data.photo)} name={displayName} size="md" />
            <div className="page__identity-text">
              <h1 className="page__title">{displayName}</h1>
              <p className="page__subtitle">
                {data.email || data.userName || t('common.none')}
              </p>
              <div className="page__badges">
                <StatusPill
                  label={data.isActive ? t('status.active') : t('status.inactive')}
                  tone={data.isActive ? 'success' : 'neutral'}
                />
                <StatusPill
                  label={
                    data.isEmailVerified ? t('status.verified') : t('status.unverified')
                  }
                  tone={data.isEmailVerified ? 'info' : 'warning'}
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="page__title">{t('agents.details.title')}</h1>
            <p className="page__subtitle">{t('agents.subtitle')}</p>
          </div>
        )}

        <div className="page__header-actions">
          <Link className="ui-button ui-button--secondary ui-button--small" to={backTo}>
            <Icon name="arrowLeft" size="sm" />
            {t('agents.details.back')}
          </Link>
          {data ? (
            <IconButton
              icon={data.isActive ? 'ban' : 'check'}
              tone={data.isActive ? 'rose' : 'emerald'}
              disabled={statusPending}
              label={
                data.isActive
                  ? t('agents.actions.deactivate')
                  : t('agents.actions.activate')
              }
              onClick={toggleActive}
            />
          ) : null}
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        </div>
      </header>

      {status === 'loading' ? <StateBlock status="loading" /> : null}
      {status === 'error' ? (
        <StateBlock status="error" message={error} onRetry={reload} />
      ) : null}

      {context ? (
        <>
          <TabNav ariaLabel={t('agents.details.tabsLabel')} items={tabs} />
          {/* aria-busy, not a spinner: a background refresh must not take the
              panel the operator is reading away from them. */}
          <div className="page__tab-panel" aria-busy={refreshing || undefined}>
            <Outlet context={context} />
          </div>
        </>
      ) : null}
    </div>
  );
}
